import { ReactNode } from "react";
import IconLoader from "/src/assets/icons/loader.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { cn } from "@/lib/utils";

type ButtonType = "primary" | "secondary" | "premium";

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix"> {
  text: string | React.ReactNode;
  variant?: ButtonType;
  prefix?: ReactNode;
  suffix?: ReactNode;
  vibrate?: boolean;
  vibrationType?:
    | "light"
    | "medium"
    | "heavy"
    | "rigid"
    | "soft"
    | "success"
    | "error"
    | "warning"
    | "selection";
  isLoading?: boolean;
}

const BaseButton = (props: ButtonProps) => {
  const {
    text,
    disabled,
    prefix,
    suffix,
    className,
    variant = "primary",
    onClick,
    vibrate = true,
    vibrationType = "soft",
    isLoading = false,
    ...restProps
  } = props;

  const buttonTheme: Record<ButtonType, string> = {
    primary: "bg-blue disabled:!bg-gray-4",
    secondary: "bg-white/08",
    premium: "bg-yellow-linear disabled:bg-none disabled:!bg-white/08",
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <VibratingButton
      className={cn(
        buttonTheme[variant],
        "outline-none text-white disabled:bg-white/08 disabled:!text-gray-1 flex-center gap-1 py-3 px-4 rounded-xl text-sm font-semibold leading-140 click-button transition-300 ",
        className
      )}
      disabled={disabled || isLoading}
      onClick={handleClick}
      vibrationType={vibrate ? vibrationType : undefined}
      {...restProps}
    >
      {isLoading ? (
        <IconLoader className="animate-spin h-5 w-5 text-white" />
      ) : (
        <>
          {prefix}
          {text}
          {suffix}
        </>
      )}
    </VibratingButton>
  );
};

export default BaseButton;
